import { ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { ReviewForm } from "@/book/components/review-form";
import { useLogout, useMe } from "@/shared/api/auth";
import { useDeleteReview, useMyReviews, type Review } from "@/shared/api/book";
import { errorMessage } from "@/shared/api/client";
import { formatDate } from "@/shared/lib/labels";
import { Button } from "@/shared/ui/button";
import { Chip } from "@/shared/ui/chip";
import { Markdown } from "@/shared/ui/markdown";
import { PageTitle } from "@/shared/ui/page-header";
import { SectionLabel } from "@/shared/ui/section-label";
import { Sheet } from "@/shared/ui/sheet";
import { ErrorNote, StatusChip } from "@/shared/ui/state";

/** A melléklet profil-oldala: beküldéseim és a könyvről írt véleményem (szerkeszthető).
 *  A fiók-szintű dolgok (név, e-mail, hírlevél, tagságok) a globális `/app/profil`-on. */
export function ProfilePage() {
  const { data: me } = useMe();
  const { data: reviews } = useMyReviews();
  const logout = useLogout();
  const navigate = useNavigate();
  const removeReview = useDeleteReview();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [editing, setEditing] = useState<Review | null>(null);
  if (!me) return null;

  const openNew = () => {
    setEditing(null);
    setReviewOpen(true);
  };

  return (
    <div className="flex flex-col gap-6">
      <PageTitle>Profil</PageTitle>

      <section className="flex flex-col gap-2">
        <SectionLabel>Beküldéseim</SectionLabel>
        <NavRow to="/app/book/tapasztalatok?who=mine">A tapasztalataim</NavRow>
        <NavRow to="/app/book/gyakorlatok?mark=favorite">Kedvenc gyakorlataim</NavRow>
        <NavRow to="/app/book/gyakorlatok?mark=want_to_try">Amiket ki akarok próbálni</NavRow>
        <NavRow to="/app/book/ajanlo?on_list=saved">Amiket tervezek az ajánlóból</NavRow>
        <NavRow to="/app/book/ajanlo?on_list=favorite">Kedvenceim az ajánlóból</NavRow>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <SectionLabel>Véleményem a könyvről</SectionLabel>
          <button
            type="button"
            onClick={openNew}
            className="text-[13px] font-semibold text-gold-text"
          >
            Írok véleményt
          </button>
        </div>
        {reviews && reviews.length > 0 ? (
          <div className="flex flex-col">
            {reviews.map((r) => (
              <div key={r.id} className="flex flex-col gap-1.5 border-t border-line py-3">
                <p className="text-[12px] text-muted">{formatDate(r.created_at)}</p>
                <Markdown className="text-[14px]">{r.body}</Markdown>
                <div className="flex flex-wrap items-center gap-1.5">
                  <StatusChip status={r.status} />
                  {r.public_ok && <Chip variant="neutral">Nyilvánosan is megjelenhet</Chip>}
                  {r.show_on_book_page && <Chip>Megjelenik a könyv oldalán</Chip>}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(r);
                      setReviewOpen(true);
                    }}
                    className="text-[13px] font-semibold text-gold-text"
                  >
                    Szerkesztem
                  </button>
                  <button
                    type="button"
                    disabled={removeReview.isPending}
                    onClick={() => {
                      if (window.confirm("Visszavonod ezt a véleményt?")) removeReview.mutate(r.id);
                    }}
                    className="text-[13px] font-semibold text-muted disabled:opacity-50"
                  >
                    Visszavonom
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[13px] text-muted">
            Ha van kedved, oszd meg, milyen volt neked a könyv.
          </p>
        )}
        {removeReview.isError && <ErrorNote>{errorMessage(removeReview.error)}</ErrorNote>}
      </section>

      <section className="flex flex-col gap-2">
        <SectionLabel>Fiók</SectionLabel>
        <NavRow to="/app/profil">Fiók, hírlevél, alkalmazásaim</NavRow>
      </section>

      <Button
        variant="outlineInk"
        block
        disabled={logout.isPending}
        onClick={() =>
          logout.mutate(undefined, {
            onSuccess: () => navigate("/app/belepes", { replace: true }),
          })
        }
      >
        Kilépek
      </Button>

      <Sheet
        open={reviewOpen}
        onOpenChange={(v) => {
          setReviewOpen(v);
          if (!v) setEditing(null);
        }}
        title={editing ? "Véleményem szerkesztése" : "Mi a véleményed a könyvről?"}
        description="Jóváhagyás után jelenik meg — ha engeded, a könyv publikus oldalán is."
      >
        <ReviewForm
          key={editing?.id ?? "new"}
          review={editing ?? undefined}
          onDone={() => {
            setReviewOpen(false);
            setEditing(null);
          }}
        />
      </Sheet>
    </div>
  );
}

function NavRow({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="flex min-h-12 items-center justify-between border-t border-line py-3 text-[15px] font-semibold"
    >
      {children}
      <ChevronRight size={20} className="text-muted" />
    </Link>
  );
}
